import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";

const EXAMPLES = [
  {
    img: "https://images.unsplash.com/photo-1676907229752-ac14b0152b92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxkcmF3ZXIlMjBvcmdhbml6ZXIlMjBzdG9yYWdlJTIwY29tcGFydG1lbnRzfGVufDB8fHx8MTc5MDc2MDg3Nnww&ixlib=rb-4.1.0&q=85",
    alt: "Organizer de tiroir avec compartiments",
    title: "Organizers / Rangement",
    desc: "Compartiments et triers sur mesure, ajustés aux dimensions exactes de vos tiroirs.",
  },
  {
    img: "https://images.unsplash.com/photo-1776395159820-922236b5adeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxjYW1wZXJ2YW4lMjBpbnRlcmlvciUyMHN0b3JhZ2V8ZW58MHx8fHwxNzkwNzYwODc2fDA&ixlib=rb-4.1.0&q=85",
    alt: "Aménagement intérieur d'un camping-car avec rangements",
    title: "Aménagements camping-car & van",
    desc: "Fixations, boîtiers, supports et optimisations d'espace pour vans et camping-cars.",
  },
  {
    img: "https://images.unsplash.com/photo-1740625940423-a59a65c753c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHwzZCUyMHByaW50ZWQlMjBwbGFzdGljJTIwb2JqZWN0c3xlbnwwfHx8fDE3OTA3NjA4NzZ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Pièces mécaniques imprimées en 3D",
    title: "Pièces de réparation",
    desc: "Engrenages, clips, charnières et pièces détachées introuvables, remodélisées à l'identique.",
  },
  {
    img: "https://images.unsplash.com/photo-1603380353725-f8a4d39cc41e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHwzZCUyMHByaW50ZWQlMjBwbGFzdGljJTIwb2JqZWN0c3xlbnwwfHx8fDE3OTA3NjA4NzZ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Imprimante 3D et prototypes sur un bureau",
    title: "Prototypes & supports techniques",
    desc: "Boîtiers, supports et fixations techniques pour vos projets makers ou professionnels.",
  },
];

export default function RealisationsPage() {
  return (
    <>
      <PageHeader
        eyebrow="Exemples de réalisations"
        title="Ce que l'impression 3D change au quotidien."
        sub="Organizers et rangements sur mesure, aménagements camping-car, pièces de réparation : quelques exemples de projets réalisables en impression et modélisation 3D."
        pageTitle="Exemples de réalisations 3D — organizer, cuisine, camping-car | TroiDéo"
      />

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {EXAMPLES.map((ex, i) => (
              <Reveal key={ex.title} delay={(i % 3) * 0.07} className="h-full">
                <article
                  data-testid={`realisation-card-${i}`}
                  className="group flex h-full flex-col overflow-hidden rounded-[20px] border border-black/10 bg-white transition-colors duration-300 hover:border-black/25"
                >
                  <div className="overflow-hidden">
                    <img
                      src={ex.img}
                      alt={ex.alt}
                      className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h2 className="font-display text-base font-bold tracking-tight text-ink sm:text-lg">{ex.title}</h2>
                    <p className="mt-1.5 flex-1 text-sm leading-relaxed text-neutral-600">{ex.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-400">
            Visuels d'illustration — chaque projet est conçu sur mesure à partir de votre demande
          </p>

          <Reveal delay={0.1}>
            <div className="corner-card relative mt-14 flex flex-col items-start justify-between gap-6 rounded-[20px] border border-accent/30 bg-orange-50 p-8 sm:flex-row sm:items-center sm:p-10">
              <div>
                <h2 className="font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">
                  Un projet en tête ?
                </h2>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-neutral-600">
                  Décrivez-le en 2 minutes : photos, croquis ou simples cotes — réponse sous 48 h
                  ouvrées avec un chiffrage détaillé.
                </p>
              </div>
              <Link
                to="/devis"
                data-testid="realisations-cta-button"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-accent"
              >
                Demander mon devis
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
